import React from 'react';
import { Box, Grid, useColorModeValue } from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';

import { BarcodeScannerOverlay } from 'clipsal-cortex-ui/src/components/BarcodeScannerOverlay';
import { CenteredLoader } from 'clipsal-cortex-ui/src/components/CenteredLoader';

import packageJson from '../package.json';
import { Banner } from './common/components/Banner';
import { RootedDeviceWarningCard } from './common/components/RootedDeviceWarningCard';
import { COMPONENT_MIN_HEIGHT, IS_DEMO_LOGIN, IS_PRODUCTION_DATA } from './common/constants';
import { useAppReady } from './common/hooks/use-app-ready';
import { useAppUpdate } from './common/hooks/use-app-update';
import { useAuthRedirect } from './common/hooks/use-auth-redirect';
import { useHighchartSetup } from './common/hooks/use-highchart-setup';
import { usePushNotificationSetup } from './common/hooks/use-push-notification-setup';
import { useRootedDeviceCheck } from './common/hooks/use-rooted-device-check';
import { useStatusBarStyleConfiguration } from './common/hooks/use-status-bar-style-configuration';
import { useViewScrollTop } from './common/hooks/use-view-scroll-top';
import { FeatureFlagsModal } from './features/account/FeatureFlagsModal';
import { SEMonitorWelcomeModal } from './features/rebranding/SEMonitorWelcomeModal';
import { selectUser } from './features/user/userSlice';

export interface LinkData {
  component: React.JSX.Element;
  path: string;
  requiresLogin: boolean;
}

export function App() {
  const { t } = useTranslation();
  const backgroundColor = useColorModeValue('#ffffff', '#111111');
  const user = useSelector(selectUser);
  useHighchartSetup();
  useStatusBarStyleConfiguration();
  useViewScrollTop();
  usePushNotificationSetup();
  /* istanbul ignore next -- @preserve */
  const isRootedDevice = useRootedDeviceCheck();
  useAppUpdate();
  useAuthRedirect();
  const isLoaded = useAppReady();

  const showStagingBanner = user.isLoggedIn && !IS_PRODUCTION_DATA && !IS_DEMO_LOGIN;

  return isRootedDevice ? (
    /* istanbul ignore next -- @preserve */
    <RootedDeviceWarningCard />
  ) : (
    <Box background={backgroundColor}>
      <Box
        style={{ backdropFilter: 'blur(6px)' }}
        width="100vw"
        background={backgroundColor}
        zIndex={20}
        position="fixed"
        height={'env(safe-area-inset-top)'}
        top="0px"
      />

      {IS_DEMO_LOGIN && (
        <Banner
          text={<Trans t={t} i18nKey="Demo Mode.message" />}
          onClick={() => {
            localStorage.removeItem('isDemoLogin');
            window.location.replace('/');
          }}
          testId="demo-login-banner"
        />
      )}

      {showStagingBanner && (
        <Banner
          text={<Trans t={t} i18nKey="Staging Mode.message" values={{ version: packageJson.version }} />}
          testId="staging-banner"
        />
      )}

      <BarcodeScannerOverlay title={t('Common.scan qr code')} />
      {isLoaded ? (
        <Box>
          <Grid minHeight={COMPONENT_MIN_HEIGHT} height="100%" templateColumns={'1fr'}>
            <Box>
              <Outlet />
            </Box>
          </Grid>
        </Box>
      ) : (
        <Box height={COMPONENT_MIN_HEIGHT}>
          <CenteredLoader />
        </Box>
      )}
      <FeatureFlagsModal />
      <SEMonitorWelcomeModal />
    </Box>
  );
}
